import React, { FC, useEffect, useRef, useState } from "react";
import { Fade, Modal } from "@mui/material";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  label: string;
  description?: string;
  defaultValue?: string;
  onDone?: (value: string) => void;
  numericOnly?: boolean;
  headerIcon?: React.ReactNode;
};

function SearchIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function CloseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function CheckCircleIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}

function HashIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 9h16" />
      <path d="M4 15h16" />
      <path d="M10 3 8 21" />
      <path d="m16 3-2 18" />
    </svg>
  );
}

function ShieldCheckIcon({ size = 25 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.68 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function ReceiptIcon({ size = 25 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
      <path d="M14 8H8" />
      <path d="M16 12H8" />
      <path d="M13 16H8" />
    </svg>
  );
}

export const SearchNumberModal: FC<Props> = ({
  open,
  onClose,
  title,
  label,
  description,
  defaultValue = "",
  onDone,
  numericOnly = false,
  headerIcon,
}) => {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue || "");
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setValue("");
    }
  }, [open, defaultValue]);

  const handleChange = (raw: string) => {
    setValue(numericOnly ? raw.replace(/\D/g, "").slice(0, 8) : raw);
  };

  const submit = () => {
    if (!value.trim()) return;
    onDone?.(value.trim());
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      sx={{ zIndex: 999 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(81,81,81,0.75)" } },
      }}
    >
      <Fade in={open} timeout={200}>
        <div className="flex h-full items-center justify-center p-6 outline-none">
          <div className="w-full max-w-[430px] overflow-hidden rounded-2xl border border-white/70 bg-white shadow-2xl">
            {/* Header */}
            <div className="relative border-b border-[#D9D9D9]/70 bg-gradient-to-b from-white to-[#F7F8F8] px-6 py-5">
              <button
                type="button"
                aria-label={`Close ${title}`}
                onClick={onClose}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-[#D9D9D9] bg-white text-[#515151] shadow-sm transition-all hover:bg-[#F3F4F4] active:scale-95"
              >
                <CloseIcon size={16} />
              </button>

              <div className="flex items-center gap-4 pr-9">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand shadow-inner">
                  {headerIcon}
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight text-[#515151]">{title}</h1>
                  {description && (
                    <p className="mt-1 text-sm text-[#515151]/65">{description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="space-y-5 px-6 py-6">
              {/* Input */}
              <div className="space-y-2">
                <label
                  htmlFor="search-modal-input"
                  className="flex items-center gap-2 text-sm font-semibold text-[#515151]"
                >
                  <span className="text-brand-secondary">
                    <HashIcon size={15} />
                  </span>
                  {label}
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    id="search-modal-input"
                    autoComplete="off"
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && value.trim()) submit();
                    }}
                    maxLength={numericOnly ? 8 : undefined}
                    inputMode={numericOnly ? "numeric" : "text"}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    className="h-12 w-full rounded-xl border border-[#D9D9D9] bg-white px-4 pr-12 text-base font-semibold tracking-wide text-[#515151] outline-none transition-all placeholder:text-[#515151]/35 focus:border-brand focus:ring-4 focus:ring-brand/10"
                  />
                  <div className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg bg-brand-secondary/10 text-brand-secondary">
                    <SearchIcon size={15} />
                  </div>
                </div>
              </div>

              {/* Info strip */}
              <div className="rounded-xl border border-[#D9D9D9]/80 bg-[#F7F8F8] p-3 shadow-inner">
                <div className="flex items-center gap-2 text-xs font-medium text-[#515151]/70">
                  <span className="text-brand">
                    <CheckCircleIcon size={15} />
                  </span>
                  {numericOnly
                    ? "Numeric input only. Press Enter or select Done to continue."
                    : "Press Enter or select Done to continue."}
                </div>
              </div>

              {/* Done button */}
              <button
                type="button"
                onClick={submit}
                disabled={!value.trim()}
                className="h-12 w-full rounded-xl bg-brand px-5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-[#D9D9D9] disabled:text-[#515151]/60"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export const PintagSearchModal: FC<Omit<Props, "title" | "label" | "description" | "numericOnly" | "headerIcon">> = (
  props
) => (
  <SearchNumberModal
    {...props}
    title="Pintag Search"
    label="Pintag No."
    description="Enter the pintag number to locate the order quickly."
    numericOnly
    headerIcon={<ShieldCheckIcon size={25} />}
  />
);

export const InvoiceSearchModal: FC<Omit<Props, "title" | "label" | "description" | "numericOnly" | "headerIcon">> = (
  props
) => (
  <SearchNumberModal
    {...props}
    title="Invoice Search"
    label="Invoice No."
    description="Enter the invoice number to locate the bill quickly."
    numericOnly
    headerIcon={<ReceiptIcon size={25} />}
  />
);
